import { MiniCalendarType } from '@lmt/shared-types';
import {
  CalendarConfigurationFormComponentProps,
  MiniCalendarTypeDictionary,
} from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import styles from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/CalendarGridSettings.module.scss';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { RadioButtonGroup } from '../../../common/ui-kit/RadioButtonGroup';

const miniCalendarTypeKeys = Object.keys(MiniCalendarType);

enum CalculateOccupancyType {
  'All',
  'Selected',
}

export const MiniCalendarSettings: React.FC<
  CalendarConfigurationFormComponentProps
> = (props) => {
  const i18nCalendar = useScopedTranslation(
    'calendar_configuration.mini_calendar',
  );
  const { register, watch, control, formState, setValue } = props.formMethods;
  const [
    miniCalendarType,
    hasFilterByPlaces,
    isShowingRoomOccupancy,
    isShowingSelectedRoomOccupancy,
    isResourceViewCalendar,
  ] = watch([
    'miniCalendarType',
    'hasFilterByPlaces',
    'isShowingRoomOccupancy',
    'isShowingSelectedRoomOccupancy',
    'isResourceViewCalendar',
  ]);

  const miniCalendarTypes: MiniCalendarTypeDictionary[] = useMemo(
    () =>
      miniCalendarTypeKeys
        .filter((key) => !Number.isNaN(Number(key)))
        .map((key) => ({
          key: Number(key),
          value: i18nCalendar.t(
            `mini_calendar_types.${MiniCalendarType[Number(key)]}` as any,
          ),
        })),
    [miniCalendarTypeKeys],
  );

  useEffect(() => {
    if (!isResourceViewCalendar) {
      setValue('isShowingRoomOccupancy', false);
    }
  }, [isResourceViewCalendar]);

  useEffect(
    function enablePlaceResourcesByDefault() {
      if (!isShowingRoomOccupancy) {
        setValue('isShowingSelectedRoomOccupancy', false);
      }
    },
    [isShowingRoomOccupancy],
  );

  return (
    <>
      <Field title={i18nCalendar.t('mini_calendar_field')}>
        <Controller
          control={control}
          name={'miniCalendarType'}
          render={({ field: { onChange, value } }) => (
            <div className={styles.input}>
              <DropDownInput
                useIdFunctionAsValue={true}
                options={miniCalendarTypes}
                value={value}
                idFunction={'key'}
                getOptionLabel={'value'}
                required={true}
                onValueChanged={(value) => {
                  onChange(value?.key);
                }}
                errorText={formState.errors.miniCalendarType?.message}
              />
            </div>
          )}
        />
      </Field>
      {miniCalendarType != MiniCalendarType.None &&
      isResourceViewCalendar &&
      hasFilterByPlaces ? (
        <Field title={''}>
          <CheckBox
            {...register('isShowingRoomOccupancy')}
            title={i18nCalendar.t('show_room_occupancy')}
          />
          {isShowingRoomOccupancy ? (
            <RadioButtonGroup
              options={[
                {
                  value: CalculateOccupancyType.All,
                  displayValue: i18nCalendar.t('all_room_occupancy'),
                },
                {
                  value: CalculateOccupancyType.Selected,
                  displayValue: i18nCalendar.t('selected_room_occupancy'),
                },
              ]}
              valueField={'value'}
              labelField={'displayValue'}
              value={
                isShowingSelectedRoomOccupancy
                  ? CalculateOccupancyType.Selected
                  : CalculateOccupancyType.All
              }
              onSelectedOptionChanged={(option) => {
                if (option?.value === CalculateOccupancyType.Selected) {
                  setValue('isShowingSelectedRoomOccupancy', true);
                } else {
                  setValue('isShowingSelectedRoomOccupancy', false);
                }
              }}
              required={true}
              radioButtonClassName={styles.radioButton}
            />
          ) : (
            <div style={{ height: 60 /* the height of a checkbox */ }} />
          )}
        </Field>
      ) : (
        <div style={{ height: 99 /* the height of a checkbox */ }} />
      )}
    </>
  );
};
